// =============================================================
// YuzuPage — Yuzu cat tab: pet the cat, view timeline, screen
// time, scrapbook, and the calendar.
// =============================================================

import { useEffect, useMemo, useRef, useState } from 'react'
import { GiftOverlay } from '../components/GiftOverlay'
import { authFetch } from '../lib/auth'
import { formatTimeZh, jstNow } from '../lib/dates'
import { showToast } from '../lib/toast'
import type { YuzuState } from '../lib/types'
import {
  ASCII_CAT_FACES,
  CAT_GIFTS,
  CAT_MESSAGES_LIST,
  INITIAL_SCRAPBOOK,
  INITIAL_SCREENTIME,
  INITIAL_YUZU_ACTIVITY,
  INITIAL_YUZU_EVENTS,
  INITIAL_YUZU_STATUS,
  LS_YUZU_EVENTS,
  LS_YUZU_PATS,
  LS_YUZU_SNACKS,
  type YuzuEvent,
} from '../lib/yuzu-data'

interface YuzuPageProps {
  active: boolean
  refreshTick?: number
}

function readLocalNumber(key: string, fallback: number): number {
  try {
    const v = parseInt(localStorage.getItem(key) || '0', 10)
    return Number.isFinite(v) && v > 0 ? v : fallback
  } catch {
    return fallback
  }
}

function readLocalEvents(): YuzuEvent[] {
  try {
    const raw = localStorage.getItem(LS_YUZU_EVENTS)
    if (!raw) return INITIAL_YUZU_EVENTS
    const parsed = JSON.parse(raw)
    if (Array.isArray(parsed)) return parsed
  } catch {
    // ignore
  }
  return INITIAL_YUZU_EVENTS
}

const PET_GLYPHS = ['❤️', '🐾', '✨', '😻', '💕']
const SNACK_GLYPHS = ['🐟', '🍖', '🍣', '✨']

export function YuzuPage({ active, refreshTick }: YuzuPageProps) {
  // Persistent state
  const [pats, setPats] = useState(() =>
    readLocalNumber(LS_YUZU_PATS, INITIAL_YUZU_STATUS.total_pats),
  )
  const [snacks, setSnacks] = useState(() =>
    readLocalNumber(LS_YUZU_SNACKS, INITIAL_YUZU_STATUS.total_snacks),
  )
  const [events, setEvents] = useState<YuzuEvent[]>(() => readLocalEvents())
  const [screentimeTotal, setScreentimeTotal] = useState(
    INITIAL_SCREENTIME.total_minutes,
  )

  // Transient / expression state
  const [catFace, setCatFace] = useState<string>(ASCII_CAT_FACES.default)
  const [exprText, setExprText] = useState<string>('呼噜噜...（=①ω①=）')
  const [speechText, setSpeechText] = useState<string>(
    '主人写 code 的样子最帅了，给你递小鱼干！🐾',
  )
  const [todayDate, setTodayDate] = useState<string>('')
  const [refreshSec, setRefreshSec] = useState(30)
  const [refreshing, setRefreshing] = useState(false)
  const [giftOpen, setGiftOpen] = useState(false)
  const [giftDesc, setGiftDesc] = useState('')

  const petAreaRef = useRef<HTMLDivElement>(null)
  const pageRef = useRef<HTMLDivElement>(null)
  const expressionResetRef = useRef<number | null>(null)
  const screentimeTotalRef = useRef(screentimeTotal)
  screentimeTotalRef.current = screentimeTotal

  // ----- Initialization -----
  useEffect(() => {
    const d = jstNow()
    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    setTodayDate(`${d.toISOString().slice(0, 10)} ${weekDays[d.getDay()]}`)
    void fetchYuzuState()
    void fetchYuzuEvents()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTick])

  // ----- Refresh countdown (30s loop, like the original) -----
  useEffect(() => {
    const id = window.setInterval(() => {
      setRefreshSec((s) => {
        if (s <= 1) {
          // Trigger refresh
          setRefreshing(true)
          window.setTimeout(() => {
            setRefreshing(false)
            // Bump screentime by 0-1 minutes
            setScreentimeTotal((t) => t + Math.floor(Math.random() * 2))
            void fetchYuzuState()
            void fetchYuzuEvents()
            // Speech bubble + toast
            if (Math.random() > 0.6) {
              const msg =
                CAT_MESSAGES_LIST[Math.floor(Math.random() * CAT_MESSAGES_LIST.length)]
              setSpeechText(msg)
              showToast('🐱 柚子猫跟你说话了哦！')
            } else {
              showToast('✨ 数据已同步最新 yuzu-cli 日志')
            }
          }, 800)
          return 30
        }
        return s - 1
      })
    }, 1000)
    return () => window.clearInterval(id)
  }, [])

  async function fetchYuzuState() {
    try {
      const res = await authFetch('/api/yuzu-state')
      if (!res.ok) return
      const data = (await res.json()) as YuzuState
      if (!data || !data.mood) return
      if (data.ascii) setCatFace(data.ascii)
      if (data.detail) setExprText(data.detail)
    } catch {
      // ignore
    }
  }

  async function fetchYuzuEvents() {
    try {
      const res = await authFetch('/api/yuzu-timeline?limit=30')
      if (!res.ok) return
      const data = (await res.json()) as YuzuEvent[]
      if (Array.isArray(data) && data.length > 0) {
        setEvents(data)
        localStorage.setItem(LS_YUZU_EVENTS, JSON.stringify(data))
      }
    } catch {
      // ignore
    }
  }

  function resetExpressionLater() {
    if (expressionResetRef.current) {
      window.clearTimeout(expressionResetRef.current)
    }
    expressionResetRef.current = window.setTimeout(() => {
      setCatFace(ASCII_CAT_FACES.default)
      setExprText('呼噜噜...（=①ω①=）')
    }, 1500)
  }

  // ----- Pet the cat (click on pet area) -----
  function handlePet(e: React.MouseEvent<HTMLDivElement>) {
    const area = petAreaRef.current
    if (!area) return
    const rect = area.getBoundingClientRect()
    const clickX = e.clientX - rect.left
    const clickY = e.clientY - rect.top

    const glyph = PET_GLYPHS[Math.floor(Math.random() * PET_GLYPHS.length)]
    const p = document.createElement('span')
    p.className = 'pet-particle'
    p.textContent = glyph
    p.style.left = clickX + 'px'
    p.style.top = clickY + 'px'
    area.appendChild(p)
    window.setTimeout(() => p.remove(), 800)

    const newPats = pats + 1
    setPats(newPats)
    localStorage.setItem(LS_YUZU_PATS, String(newPats))

    setCatFace(ASCII_CAT_FACES.happy)
    setExprText('(*✧ω✧*) 喵哈哈! 好舒服喵~')

    if (Math.random() > 0.4) {
      const msg = CAT_MESSAGES_LIST[Math.floor(Math.random() * CAT_MESSAGES_LIST.length)]
      setSpeechText(msg)
    }

    if (newPats % 10 === 0) {
      const newEvent: YuzuEvent = {
        ts: new Date().toISOString(),
        type: 'pet',
        detail: `琥珀疯狂摸了 ${newPats} 次柚子猫 ✨`,
      }
      const next = [newEvent, ...events].slice(0, 20)
      setEvents(next)
      localStorage.setItem(LS_YUZU_EVENTS, JSON.stringify(next))
      showToast('💕 达成摸猫里程碑！')
    }

    resetExpressionLater()
  }

  // ----- Feed the cat -----
  function handleFeed(e: React.MouseEvent<HTMLButtonElement>) {
    e.stopPropagation()
    const page = pageRef.current
    if (!page) return
    const targetRect = e.currentTarget.getBoundingClientRect()
    const containerRect = page.getBoundingClientRect()
    const clickX = targetRect.left - containerRect.left + targetRect.width / 2
    const clickY = targetRect.top - containerRect.top

    for (let i = 0; i < 3; i++) {
      const glyph = SNACK_GLYPHS[Math.floor(Math.random() * SNACK_GLYPHS.length)]
      const p = document.createElement('span')
      p.className = 'pet-particle'
      p.textContent = glyph
      p.style.left = clickX + Math.random() * 40 - 20 + 'px'
      p.style.top = clickY + Math.random() * 20 - 10 + 'px'
      page.appendChild(p)
      window.setTimeout(() => p.remove(), 800)
    }

    const newSnacks = snacks + 1
    setSnacks(newSnacks)
    localStorage.setItem(LS_YUZU_SNACKS, String(newSnacks))

    setCatFace(ASCII_CAT_FACES.snack)
    setExprText('(˘ꈊ˘) 喵! 嚼吧嚼吧超好吃 🐟')

    const newEvent: YuzuEvent = {
      ts: new Date().toISOString(),
      type: 'snack',
      detail: '喂食成功！柚子猫吃掉了一只小鱼干 🐟',
    }
    const next = [newEvent, ...events].slice(0, 20)
    setEvents(next)
    localStorage.setItem(LS_YUZU_EVENTS, JSON.stringify(next))

    const gift = CAT_GIFTS[Math.floor(Math.random() * CAT_GIFTS.length)]
    setGiftDesc(gift)
    setGiftOpen(true)
    setSpeechText('嚼吧嚼吧… 超好吃呀主人！再赏一根喵~ 🐾')
    resetExpressionLater()
  }

  function closeGift() {
    setGiftOpen(false)
    showToast('🐟 投喂成功！获得猫猫好感')
  }

  // ----- Memoized derived data -----
  const sortedEvents = useMemo(() => [...events].reverse().slice(0, 15), [events])

  // ----- Render -----
  return (
    <>
      <div className={`page ${active ? 'active' : ''}`} ref={pageRef}>
        <div className="page-inner yuzu-page-container">
          {/* Header */}
          <div className="yuzu-header">
            <div className="yuzu-title-group">
              <span className="yuzu-title-icon">🐱</span>
              <div className="yuzu-title-text">
                <h2>柚子猫日報</h2>
                <p>{todayDate}</p>
              </div>
            </div>
            <div className="yuzu-days-counter">
              <span className="yuzu-days-num">{INITIAL_YUZU_STATUS.days_together}</span>
              <span className="yuzu-days-label">Days Together</span>
            </div>
          </div>

          {/* Status card */}
          <div className="yuzu-status-card">
            <div
              className="pet-area"
              ref={petAreaRef}
              onClick={handlePet}
            >
              <div className="pet-helper">🐾 抚摸我</div>
              <pre className="ascii-cat">{catFace}</pre>
              <div className="cat-expr-text">{exprText}</div>
            </div>
            <div className="speech-bubble">{speechText}</div>
            <div className="status-action-row">
              <div className="status-indicator">
                <span className="status-pulse" />
                <span>{INITIAL_YUZU_ACTIVITY.status}</span>
              </div>
              <button className="btn-feed" type="button" onClick={handleFeed}>
                🐠 投喂小鱼干
              </button>
            </div>
          </div>

          {/* Timeline */}
          <div className="timeline-card">
            <div
              className="timeline-title"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span>今日时光轨迹</span>
              <div className="refresh-dial">
                <span className={refreshing ? 'refresh-spin' : ''}>🔄</span>
                <span>{refreshSec}s</span>
              </div>
            </div>
            <div className="timeline-container">
              {sortedEvents.map((evt, i) => (
                <div className="timeline-item" key={i}>
                  <div className="timeline-dot" />
                  <div className="timeline-content">
                    <div className="timeline-time">
                      {formatTimeZh(new Date(evt.ts))}
                    </div>
                    <div className="timeline-text">
                      {emojiFor(evt.type)} {evt.detail || defaultDetail(evt.type)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div
              className="day-sec-title"
              style={{ marginTop: 16, marginBottom: 8 }}
            >
              📚 今日专属课程表
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 8,
              }}
            >
              {INITIAL_YUZU_ACTIVITY.today_classes.map((cls, i) => (
                <div
                  key={i}
                  style={{
                    background: 'var(--ivory-light)',
                    border: '1px solid var(--ivory-dark)',
                    padding: 8,
                    borderRadius: 'var(--radius-md)',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <span
                    style={{
                      fontWeight: 700,
                      fontSize: 11,
                      color: 'var(--slate-darkest)',
                    }}
                  >
                    {cls.name}
                  </span>
                  <span
                    style={{
                      fontSize: 9,
                      color: 'var(--cloud-dark)',
                      marginTop: 2,
                    }}
                  >
                    {cls.start} - {cls.end}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Screen time */}
          <div className="screentime-card">
            <div className="screentime-header">
              <span className="screentime-title">📱 屏幕使用统计</span>
              <div>
                <span className="screentime-total">{screentimeTotal}</span>
                <span className="screentime-total-unit">分钟</span>
              </div>
            </div>
            <div>
              {INITIAL_SCREENTIME.categories.map((cat) => {
                const pct = Math.round((cat.minutes / INITIAL_SCREENTIME.total_minutes) * 100)
                return (
                  <div className="screentime-bar-group" key={cat.name}>
                    <div className="screentime-bar-info">
                      <span>
                        {cat.icon} {cat.name}
                      </span>
                      <span
                        style={{
                          color: 'var(--cloud-dark)',
                          fontFamily: 'monospace',
                        }}
                      >
                        {cat.minutes}分 ({pct}%)
                      </span>
                    </div>
                    <div className="screentime-bar-bg">
                      <div
                        className={`screentime-bar-fill ${cat.color}`}
                        style={{ width: pct + '%' }}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Scrapbook */}
          <div className="scrapbook-card">
            <div className="timeline-title">🌸 温暖纪念册</div>
            <div className="scrapbook-grid">
              {INITIAL_SCRAPBOOK.map((m, i) => (
                <div className="scrapbook-item" key={i}>
                  <span className="scrapbook-date">{m.date}</span>
                  <span className="scrapbook-text">{m.detail}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Stats footer */}
          <div className="stats-footer" style={{ marginBottom: 24 }}>
            <div className="stats-footer-box">
              <div className="stats-footer-label">累计抚摸 💖</div>
              <div className="stats-footer-val">{pats}</div>
            </div>
            <div className="stats-footer-box">
              <div className="stats-footer-label">累计小鱼干 🐟</div>
              <div className="stats-footer-val">{snacks}</div>
            </div>
          </div>
        </div>
      </div>

      <GiftOverlay open={giftOpen} desc={giftDesc} onClose={closeGift} />
    </>
  )
}

function emojiFor(type: string): string {
  switch (type) {
    case 'wake':
      return '🌅'
    case 'edit':
      return '💻'
    case 'commit':
      return '🚀'
    case 'pet':
      return '💖'
    case 'snack':
      return '🐟'
    case 'class':
      return '📚'
    case 'breath':
      return '🥱'
    case 'hold':
      return '📦'
    case 'cream_puff':
      return '🧁'
    case 'talk':
      return '💬'
    case 'play':
      return '🎮'
    case 'squeeze':
      return '🤗'
    case 'phone':
      return '📱'
    default:
      return '🐾'
  }
}

function defaultDetail(type: string): string {
  switch (type) {
    case 'wake':
      return '琥珀起床了'
    case 'commit':
      return '琥珀完成了一次 commit'
    case 'breath':
      return '柚子猫打了个哈欠'
    case 'hold':
      return '琥珀存了一条记忆'
    case 'cream_puff':
      return '变成奶油泡芙了'
    case 'pet':
      return '琥珀摸了摸柚子猫'
    case 'snack':
      return '柚子猫吃了小鱼干'
    case 'talk':
      return '和柚子猫聊天了'
    case 'play':
      return '和柚子猫玩耍了'
    case 'squeeze':
      return '琥珀捏了捏柚子猫'
    default:
      return '柚子猫日常'
  }
}